import type { PersonEntry } from '../components/PeopleHoverCard';
import { PurchaseOrderApi } from '../types';

// A whole PO (PO list) or one calendar day's slice of it (calendar).
type PeopleSource = Pick<PurchaseOrderApi, 'assignments' | 'completed_inspections'>;

export const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

export const getAvatarColor = (name: string) => {
  const colors = [
    'bg-indigo-100 text-indigo-700',
    'bg-pink-100 text-pink-700',
    'bg-blue-100 text-blue-700',
    'bg-purple-100 text-purple-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const toPerson = (p: Omit<PersonEntry, 'initials' | 'color'>): PersonEntry => ({
  ...p,
  initials: getInitials(p.name),
  color: getAvatarColor(p.name),
});

// Newest first, so the first row seen per person carries their latest date.
// Reassigning updates the row in place, so updated_at is when it was last assigned.
const getAssignmentsNewestFirst = (po: PeopleSource) =>
  [...(po.assignments || [])].sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));

// Finalising an inspection deletes its assignment, so people from completed
// inspections (latest first) follow the current ones. Someone still assigned
// appears once, as assigned. The first person is the one shown in the cell.
const mergePeople = (current: PersonEntry[], completed: PersonEntry[]) => {
  const seen = new Set(current.map(p => p.key));
  const past = completed.filter(p => {
    if (seen.has(p.key)) return false;
    seen.add(p.key);
    return true;
  });
  return [...current, ...past];
};

export const getAssignedToPeople = (po: PeopleSource) => {
  const current = new Map<string, PersonEntry>();
  getAssignmentsNewestFirst(po).forEach(a => {
    if (a.inspector && !current.has(a.inspector.id)) {
      current.set(a.inspector.id, toPerson({
        key: a.inspector.id, name: a.inspector.name, photo: a.inspector.profileImage,
        status: 'assigned', date: a.updated_at || null,
      }));
    }
  });
  const completed = (po.completed_inspections || []).flatMap(ci => ci.inspector_name
    ? [toPerson({
        key: ci.inspector_id || ci.inspector_name, name: ci.inspector_name, photo: ci.inspector_profile_image,
        status: 'completed', date: ci.completed_at,
      })]
    : []);
  return mergePeople([...current.values()], completed);
};

export const getAssignedByPeople = (po: PeopleSource) => {
  const current = new Map<string, PersonEntry>();
  const assignees = new Map<string, Set<string>>();
  getAssignmentsNewestFirst(po).forEach(a => {
    if (!a.assignedBy) return;
    if (!current.has(a.assignedBy.id)) {
      current.set(a.assignedBy.id, toPerson({
        key: a.assignedBy.id, name: a.assignedBy.name, photo: a.assignedBy.profileImage,
        status: 'assigned', date: a.updated_at || null,
      }));
    }
    const names = assignees.get(a.assignedBy.id) || new Set<string>();
    if (a.inspector) names.add(a.inspector.name);
    assignees.set(a.assignedBy.id, names);
  });
  current.forEach((p, id) => { p.detail = [...(assignees.get(id) || [])].join(', ') || undefined; });
  const completed = (po.completed_inspections || []).flatMap(ci => ci.assigned_by && ci.assigned_by_name
    ? [toPerson({
        key: ci.assigned_by, name: ci.assigned_by_name, photo: ci.assigned_by_profile_image,
        status: 'completed', date: ci.completed_at, detail: ci.inspector_name || undefined,
      })]
    : []);
  return mergePeople([...current.values()], completed);
};
